// components
import {
  getSourceFields,
  getFieldOptionLabel
} from 'components/Modals/SystemProcessModal/Form/components/Transactions/components/Transaction/components/InjuryMasterMapping/utils'
// types
import {
  IField,
  IRiskTemplate,
  IInjuryMasterPropertyMapping,
  IInjuryMasterPropertyMappingInput,
  ISystemProcessTransactionCreateCaseInput,
  ISystemProcessTransactionUpdateCaseInput
} from 'types/graphql'
// utils
import { getValueFromOption } from 'utils/form'

// Where a mapping's value comes from — the alternative to reading a Skefto field.
export const FIXED_VALUE_SOURCE = 'FIXED'

interface IMappingFormValue {
  property?: { value: string }
  // FIXED_VALUE_SOURCE when the value is set on the mapping; otherwise it is read from sourceField
  source?: { value: string }
  sourceField?: { value: string }
  // An option for a choice or yes/no, text for the rest, an option for a single Injury Master
  // record, or several options (RecordPicker's isMulti) for a record list
  fixedValue?: { value: string } | { value: string }[] | string
  // Skefto option id -> chosen Injury Master option (its code as the value)
  valueMap?: Record<string, { value: string } | null | undefined>
  // Skefto option id -> chosen Injury Master record: an option once RecordMap has resolved it, or
  // still the raw saved URL in the brief window before that (see RecordMap.tsx)
  recordMap?: Record<string, { value: string } | string | null | undefined>
}

// A record list's fixed value is several records (RecordPicker's isMulti) — sent as a JSON array of
// their URLs, since InjuryMasterPropertyMappingInput.fixedValue is a single string.
const getFixedValue = (fixedValue: IMappingFormValue['fixedValue']): string => {
  if (typeof fixedValue === 'string') {
    return fixedValue
  }

  if (Array.isArray(fixedValue)) {
    return JSON.stringify(fixedValue.map((option) => option.value))
  }

  return fixedValue?.value
}

const getMappingsInput = (mappings: IMappingFormValue[] = []): IInjuryMasterPropertyMappingInput[] =>
  mappings.map((mapping) => {
    const property = getValueFromOption(mapping.property)

    if (mapping.source?.value === FIXED_VALUE_SOURCE) {
      return { property, fixedValue: getFixedValue(mapping.fixedValue) }
    }

    return {
      property,
      fieldId: getValueFromOption(mapping.sourceField),
      valueMap: Object.entries(mapping.valueMap ?? {})
        .filter(([, option]) => option?.value !== undefined && option?.value !== null)
        .map(([from, option]) => ({ from, to: Number(option.value) })),
      recordMap: Object.entries(mapping.recordMap ?? {})
        .map(([from, option]) => ({ from, to: typeof option === 'string' ? option : option?.value }))
        .filter((entry): entry is { from: string; to: string } => !!entry.to)
    }
  })

// Create and update take the same input: which Skefto fields fill which Injury Master properties.
export const getCaseInput = (
  transaction: Record<string, any>
): ISystemProcessTransactionCreateCaseInput & ISystemProcessTransactionUpdateCaseInput => ({
  propertyMappings: getMappingsInput(transaction.propertyMappings)
})

// The form holds the property key and the choice code as both value and label; the mapping UI swaps
// in the readable names once the catalog of Injury Master properties has loaded.
export const getMappingsInitialValues = (
  mappings: IInjuryMasterPropertyMapping[] | null | undefined,
  templateFields: IField[],
  // A parent case transaction may also map fields of the form's parent templates
  parentTemplates: IRiskTemplate[] = []
) => {
  const fields = getSourceFields(templateFields, parentTemplates)

  return (mappings ?? []).map((mapping) => {
    const field = fields.find(({ id }) => id === mapping.fieldId)

    if (mapping.fixedValue !== undefined && mapping.fixedValue !== null) {
      return {
        property: { value: mapping.property, label: mapping.property },
        source: { value: FIXED_VALUE_SOURCE, label: 'A fixed value' },
        // The mapping UI turns this into an option once it knows whether the property is a choice
        fixedValue: mapping.fixedValue
      }
    }

    return {
      property: { value: mapping.property, label: mapping.property },
      sourceField: field
        ? { value: field.id, label: getFieldOptionLabel(fields, field, parentTemplates), field }
        : undefined,
      valueMap: Object.fromEntries(
        (mapping.valueMap ?? []).map(({ from, to }) => [from, { value: String(to), label: String(to) }])
      ),
      // Left as raw URLs — RecordMap resolves each to its name (there is no lookup-by-url on the
      // list endpoints for the mapping UI to use, so this is a request of its own).
      recordMap: Object.fromEntries((mapping.recordMap ?? []).map(({ from, to }) => [from, to]))
    }
  })
}
